import {
  Icon,
  Typography,
  Toastify,
  toast,
  Input,
  PopUpModal,
} from "top-brains-ui";
import styles from "./login.module.scss";
import { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  clearIntendedPath,
  setOrgLogo,
  setParentIdByLevel,
  setTheme,
  setUserDetails,
  setUserLogin,
} from "@src/app/globalSlice";
import {
  LOGIN_PAGE_CONSTANTS,
  USER_CONSTANTS,
  VALIDATION_CONSTANTS,
} from "@src/constants/UiConstants";
import { LoginInitialValues } from "./types";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useClientLoginMutation,
  useLoginMutation,
  useMulti_rolesMutation,
  useSaveUserMutation,
} from "@src/api/LoginApiSlice";
import { setTokens } from "@src/app/authSlice";
import { ICON_COLOR_CONSTANTS } from "@src/styles/ColorConstants";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import GradiantButton from "@src/components/gradiant-btn/GradiantButton";
import { RootState } from "@src/app/store";
import { mapLevel, validateTime } from "@src/utils/CommonUtils";
import { setSelectedMenu } from "@src/app/headersSlice";
import { isElectronApp, isNewLandingEnabled } from "@src/utils/envUtils";
import { clearLogoutSession } from "@src/utils/logoutUtils";
import ElectronLogin from "./ElectronLogin";

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const from = location.state?.from || "/";
  const [login, { data, error, isLoading }] = useLoginMutation();
  const [clientLogin, { isLoading: isClientLoginLoading }] =
    useClientLoginMutation();
  const [saveUser, { isLoading: isSaveUserLoading }] = useSaveUserMutation();
  const [multi_roles, { isLoading: isMultiRolesLoading }] =
    useMulti_rolesMutation();
  const [isMultiRolesFetched, setIsMultiRolesFetched] = useState(false);
  const { intendedPath, theme } = useSelector(
    (state: RootState) => state.globalSlice,
  );
  const isElectron = isElectronApp();

  const isAnyLoading =
    isSubmitting ||
    isLoading ||
    isClientLoginLoading ||
    isSaveUserLoading ||
    isMultiRolesLoading;

  const inputVariant = isElectron
    ? (theme === "app-theme" ? "primary" : "website")
    : "website";

  const loginInitialValues: LoginInitialValues = {
    email: "",
    password: "",
    rememberMe: false,
  };

  const loginValidationSchema = Yup.object({
    email: Yup.string()
      .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
      .test("dot-in-domain", "Invalid email", (value) => {
        if (!value) return false;
        const parts = value.split("@");
        return parts.length === 2 && parts[1].includes(".");
      })
      .required(VALIDATION_CONSTANTS.EMAIL_REQUIRED),
    password: Yup.string()
      .min(8, VALIDATION_CONSTANTS.PASSWORD_MIN_LENGTH)
      .required(VALIDATION_CONSTANTS.PASSWORD_REQUIRED),
  });

  const handleSubmit = async (
    values: LoginInitialValues,
    formikHelpers?: any,
  ) => {
    if (isAnyLoading) {
      return;
    }
    setIsSubmitting(true);
    if (!isElectron) {
      try {
        const isTimeValid = await validateTime();
        if (!isTimeValid?.state) {
          toast.error(
            isTimeValid?.message ||
              "Your system time is incorrect. Please sync to IST.",
          );
          setIsSubmitting(false);
          formikHelpers?.setSubmitting?.(false);
          return;
        }
      } catch (err) {
        toast.error("Network issue, please try again.");
        setIsSubmitting(false);
        formikHelpers?.setSubmitting?.(false);
        return;
      }
    }
    const credentials = {
      userEmail: values?.email?.toLowerCase(),
      password: values.password,
      ...(isElectron && { isClient: true }),
    };
    const clientCredentials = {
      userEmail: values?.email?.toLowerCase(),
      password: values.password,
    };
    try {
      if (isElectron && navigator?.onLine) {
        const clientResult = await clientLogin(clientCredentials).unwrap();
        const { user, student } = clientResult?.response ?? {};
        if (user && student) {
          await saveUser({
            user: {
              createdOn: user.createdOn,
              createdByName: user.createdByName,
              createdByEmail: user.createdByEmail,
              id: user.id,
              name: user.name,
              email: user.email,
              phoneNumber: user.phoneNumber,
              password: user.password,
              privileges: user.privileges,
              activationStatus: user.activationStatus,
              gender: user.gender,
              subjectIds: user.subjectIds,
              currentPrivilege: user.currentPrivilege,
              sessionId: user.sessionId,
              username: user.username,
            },
            student: {
              id: student.id,
              batches: student.batches,
              courses: student.courses,
              organizationIds: student.organizationIds,
              requestedOrgs: student.requestedOrgs,
              requestedSubOrgs: student.requestedSubOrgs,
              requestedBranches: student.requestedBranches,
              currentOrganization: student.currentOrganization,
              subOrganizationIds: student.subOrganizationIds,
              clientIds: student.clientIds,
              branchIds: student.branchIds,
              quizReferences: student.quizReferences,
              tournaments: student.tournaments,
              globalQuizzes: student.globalQuizzes,
            },
          });
        }
        await login(credentials).unwrap();
      } else {
        await login(credentials).unwrap();
      }
      toast.success("User LoggedIn SuccessFully");
    } catch (err) {
      setIsSubmitting(false);
      formikHelpers?.setSubmitting?.(false);
      console.error("Login failed:", err);
      const errorMessage =
        (err as { data?: { response?: string } })?.data?.response ??
        "Network Issue, Please try again later";
      toast.error(errorMessage);
    }
  };

  const handleForgetPassword = () => {
    dispatch(setUserLogin(false));
    navigate("/forgot-password");
  };

  const handleDirectExit = () => {
    if (isElectron) {
      clearLogoutSession(dispatch, { theme: "app-theme" });
      (window as any).electronApp.quitApp();
    }
  };

  const navigateBasedOnIntendedPath = (
    userPrivileges: string[],
    loginData: any,
  ) => {
    if (isNewLandingEnabled()) {
      dispatch(setUserLogin(false));
      navigate("/landing-portal", { replace: true });
      return;
    }

    if (userPrivileges.length === 1) {
      if (
        !isElectron &&
        intendedPath?.key &&
        (intendedPath.key === "challenges" ||
          intendedPath.key === "quiz" ||
          intendedPath.key === "tournament" ||
          intendedPath.key === "home")
      ) {
        navigate(intendedPath.path, { replace: true });
        dispatch(setUserLogin(false));
      } else if (userPrivileges[0] === USER_CONSTANTS.STUDENT) {
        if (isElectron) {
          dispatch(setTheme("app-theme"));
          const agreed =
            loginData?.agreedTermsAndCondition ??
            loginData?.user?.agreedTermsAndCondition ??
            data?.response?.userLoginResponse?.user?.agreedTermsAndCondition;
          if (agreed !== true) {
            navigate("/terms-and-conditions");
          } else {
            navigate("/learn");
          }
        } else {
          navigate("/student-organization", { replace: true });
        }
      } else if (userPrivileges[0] === USER_CONSTANTS.TASK_MANAGER) {
        dispatch(setTheme("app-theme"));
        navigate("/quiz", { replace: true });
      } else if (userPrivileges[0] === USER_CONSTANTS.SUPER_TASK_MANAGER) {
        dispatch(setTheme("app-theme"));
        navigate("/organizations", { replace: true });
      } else if (userPrivileges[0] === USER_CONSTANTS.ADMIN) {
        dispatch(setTheme("app-theme"));
        if (loginData.organizationType === "SUB_ORGANIZATION") {
          dispatch(setSelectedMenu(loginData?.organizationName));
          navigate("/organizations/sub-organization/branch");
        } else if (loginData?.organizationType === "BRANCH") {
          dispatch(setSelectedMenu(loginData?.organizationName));
          navigate("/organizations/branch/branch-dashboard");
        } else if (loginData.organizationType === "CLIENT") {
          dispatch(setSelectedMenu(loginData?.organizationName));
          navigate("/organizations/clients/batch");
        } else {
          dispatch(setTheme("app-theme"));
          navigate("/subjects");
        }
      } else {
        dispatch(setTheme("app-theme"));
        navigate("/subjects", { replace: true });
      }
    } else if (
      userPrivileges.length > 1 &&
      userPrivileges.includes(USER_CONSTANTS.STUDENT)
    ) {
      navigate("/choose-role", { replace: true });
    } else {
      navigate("/multi_roles", { replace: true });
    }
    dispatch(setUserLogin(false));
  };

  useEffect(() => {
    const fetchMultiRoles = async () => {
      try {
        if (isMultiRolesFetched) return;
        setIsMultiRolesFetched(true);

        const responseNew = await multi_roles({
          currentPrivilege:
            data?.response?.userLoginResponse?.user?.privileges[0],
          userId: data?.response?.userLoginResponse?.user?.id,
        });

        if (responseNew) {
          const userObj = {
            ...responseNew?.data?.response,
            agreedTermsAndCondition:
              responseNew?.data?.response?.agreedTermsAndCondition ??
              data?.response?.userLoginResponse?.user?.agreedTermsAndCondition,
          };
          dispatch(setUserDetails(userObj));
          dispatch(
            setParentIdByLevel({
              level: mapLevel(userObj?.organizationType),
              id: userObj?.organizationId,
              name: userObj?.organizationName,
            }),
          );
          dispatch(setOrgLogo(userObj?.logo as string));
          const userPrivileges = userObj?.privileges || [];
          navigateBasedOnIntendedPath(
            userPrivileges,
            userObj,
          );
        }
      } catch (err) {
        setIsSubmitting(false);
        console.error("Error fetching multi_roles details:", err);
      }
    };

    if (data) {
      sessionStorage.removeItem("hasAutoRedirected");
      const user = data?.response?.userLoginResponse?.user;
      if (isElectron && user?.expiryTime) {
        const timeString =
          user?.expiryTime?.replace(" IST", "")?.replace(" ", "T") + "+05:30";
        const expiryDate = new Date(timeString);
        if (new Date() > expiryDate) {
          setShowExpiryModal(true);
          return;
        }
      }
      dispatch(setUserDetails(data?.response?.userLoginResponse?.user));
      dispatch(
        setParentIdByLevel({
          level: mapLevel(data?.response?.userLoginResponse?.organizationType),
          id: data?.response?.userLoginResponse?.organizationId,
          name: data?.response?.userLoginResponse?.organizationName,
        }),
      );
      dispatch(setTokens(data?.response?.userLoginResponse));

      const userPrivileges =
        data?.response?.userLoginResponse?.user?.privileges || [];
      if (
        from.startsWith("/join-group/") ||
        from.startsWith("/quiz/assign/") ||
        from.startsWith("/v1/quiz/assign/")
      ) {
        dispatch(setUserLogin(false));
        navigate(from, { replace: true });
        return;
      }
      if (from.startsWith("/quiz/invite") || from.startsWith("/v1/quiz/invite")) {
        const hasOrgId = from.startsWith("/v1/quiz/invite/")
          ? Boolean(from.replace("/v1/quiz/invite/", "").trim())
          : from.startsWith("/quiz/invite/")
            ? Boolean(from.replace("/quiz/invite/", "").trim())
            : false;

        dispatch(setUserLogin(false));
        if (hasOrgId) {
          navigate(from, { replace: true });
          return;
        } else {
          navigate("/website-quiz", { replace: true });
        }
      }
      if (userPrivileges.length === 1) {
        fetchMultiRoles();
      } else {
        navigateBasedOnIntendedPath(
          userPrivileges,
          data?.response?.userLoginResponse,
        );
      }
    }
    if (error) {
      setIsSubmitting(false);
      console.error("Error fetching user details:", error);
      if ("data" in error) {
        const data = (error as FetchBaseQueryError).data as {
          response: string;
        };
        toast.error(data?.response);
      }
    }
  }, [
    data,
    error,
    dispatch,
    navigate,
    isMultiRolesFetched,
    intendedPath,
    from,
  ]);

  if (isElectron) {
    return (
      <ElectronLogin
        onSubmit={handleSubmit}
        isLoading={isAnyLoading}
        onExit={handleDirectExit}
        showExpiryModal={showExpiryModal}
      />
    );
  }

  return (
    <>
      <div
        className={`${styles.loginContainer} ${isElectron ? styles.electronApp : ""} ${
          isElectron ? (theme === "app-theme" ? styles.appTheme : styles.websiteTheme) : ""
        }`}
      >
        <div className={styles.logicLeftCard}>
          <div className={styles.logoContainer}>
            <div>
              <Typography
                fontWeight="semi-bold"
                textAlign="center"
                fontSize={24}
                color={
                  isElectron
                    ? "var(--primary-btn-bg-color)"
                    : "var(--modal-heading-color)"
                }
              >
                {LOGIN_PAGE_CONSTANTS.WELCOME_HEADING}
              </Typography>
            </div>
            <div>
              <Typography
                fontWeight="medium"
                textAlign="center"
                fontSize={12}
                color={
                  isElectron
                    ? "var(--app-text-secondary)"
                    : "var(--modal-sub-heading-color)"
                }
              >
                {LOGIN_PAGE_CONSTANTS.WELCOME_DESCRIPTION}
              </Typography>
            </div>
          </div>
          <div className={styles.loginFormContainer}>
            <Formik
              initialValues={loginInitialValues}
              validationSchema={loginValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, handleChange, handleBlur, isSubmitting: formikSubmitting }) => (
                <Form autoComplete="off">
                  <div className={styles.formFields}>
                    <div className={styles.formErrorField}>
                      <Field
                        as={Input}
                        name="email"
                        label={LOGIN_PAGE_CONSTANTS.EMAIL_LABEL}
                        required
                        type="email"
                        placeholder={LOGIN_PAGE_CONSTANTS.EMAIL_PLACEHOLDER}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={styles.formFields}
                        value={values.email}
                        variant={inputVariant}
                        autoComplete="off"
                      />
                      <Typography fontSize={10} className={styles.errorText}>
                        {errors.email}
                      </Typography>
                    </div>
                    <div className={styles.formErrorField}>
                      <div className={styles.passwordFieldWrapper}>
                        <Field
                          as={Input}
                          name="password"
                          label={LOGIN_PAGE_CONSTANTS.PASSWORD_LABEL}
                          required
                          type={showPassword ? "text" : "password"}
                          placeholder={
                            LOGIN_PAGE_CONSTANTS.PASSWORD_PLACEHOLDER
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          variant={inputVariant}
                          autoComplete="off"
                        />
                        <span
                          onClick={() => setShowPassword((prev) => !prev)}
                          className={styles.passwordIcon}
                        >
                          {showPassword ? (
                            <Icon
                              name="view_access_icon"
                              color={
                                isElectron
                                  ? "var(--app-text-secondary)"
                                  : ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON
                              }
                            />
                          ) : (
                            <Icon
                              name="hide_access_icon"
                              color={
                                isElectron
                                  ? "var(--app-text-secondary)"
                                  : ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON
                              }
                            />
                          )}
                        </span>
                      </div>

                      <Typography fontSize={8} className={styles.errorText}>
                        {errors.password}
                      </Typography>
                    </div>
                  </div>
                  <div className={styles.loginBtn}>
                    <GradiantButton
                      variant="primary"
                      type="submit"
                      width="100%"
                      height="40px"
                      fontSize={16}
                      labelClassName={styles.buttonLabel}
                      label={LOGIN_PAGE_CONSTANTS.LOGIN_BUTTON}
                      disabled={isAnyLoading || formikSubmitting}
                      isLoading={isAnyLoading || formikSubmitting}
                      loadingLabel="Signing In..."
                    />
                    {!isElectron && (
                      <div>
                        <Typography
                          color="var(--website-text-color4)"
                          onClick={handleForgetPassword}
                          fontSize={16}
                          fontWeight="medium"
                          cursor="pointer"
                        >
                          {LOGIN_PAGE_CONSTANTS.FORGOT_PASSWORD}
                        </Typography>
                      </div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <Toastify />
      {showExpiryModal && (
        <PopUpModal
          isOpen={showExpiryModal}
          onClose={() => {}}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          titleMessage="Application Expired"
          iconName="warning"
          subTitleMessage=""
          modalMessage={
            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <Typography as="p" fontSize={16}>
                Your application access has expired.
              </Typography>
              <Typography as="p" fontSize={16}>
                You cannot proceed further. Please exit the application.
              </Typography>
            </div>
          }
          onContinue={() => {
            if (isElectron) {
              clearLogoutSession(dispatch, { theme: "app-theme" });
              (window as any).electronApp.quitApp();
            }
          }}
          showCloseIcon={false}
          showFirstButton={false}
          secondButtonLabel="Exit"
        />
      )}
    </>
  );
};

export default Login;
